export const TERMS_CONTENT = {
  es: {
    pageTitle: "Términos y Condiciones – Cyrqle",
    metaDescription:
      "Términos de Cyrqle: app familiar, requisitos de edad, consentimiento del círculo y reglas de uso.",
    lastUpdated: "Última actualización: junio 2026",
    back: "Volver al inicio",
    intro:
      "Estos términos regulan el acceso y uso de Cyrqle, una aplicación familiar de bienestar. Al utilizar el servicio, aceptas cumplir las condiciones descritas a continuación, incluidos los requisitos de edad y consentimiento para los datos de tu círculo.",
    contactEmail: "legal@sentinelzone.com",
    sections: [
      {
        title: "1. Objeto del servicio",
        paragraphs: [
          "Cyrqle es una aplicación familiar de bienestar orientada a que un adulto responsable organice, interprete y comprenda datos de salud de su círculo cercano.",
          "Cyrqle no presta servicios médicos, no emite diagnósticos ni prescribe tratamientos.",
        ],
      },
      {
        title: "2. Elegibilidad y cuenta",
        paragraphs: ["Para utilizar Cyrqle y subir datos debes:"],
        listItems: [
          "Tener al menos 18 años cumplidos",
          "Ser el titular de la cuenta y la única persona autorizada para subir o importar datos de salud",
          "Proporcionar información veraz y mantenerla actualizada",
          "Proteger el acceso a tu cuenta y notificar usos no autorizados",
        ],
      },
      {
        title: "3. Círculo familiar y consentimiento",
        paragraphs: [
          "Cyrqle permite gestionar datos de salud de miembros de tu círculo familiar: pareja, padres, hijos y otros allegados.",
          "La responsabilidad sobre la legitimidad de esos datos recae en el titular de la cuenta.",
        ],
        listItems: [
          "Puedes introducir datos de menores de edad cuando actúas como progenitor, tutor legal o responsable legitimado.",
          "Si subes datos de personas mayores de 18 años, debes contar con su consentimiento expreso e informado antes de hacerlo.",
          "Sin ese consentimiento, o si no puedes acreditarlo cuando te lo solicitemos, podemos restringir, suspender o limitar funciones de tu cuenta hasta regularizar la situación.",
          "Queda prohibido utilizar la app para gestionar datos de adultos de tu círculo sin su autorización.",
        ],
      },
      {
        title: "4. Uso permitido",
        paragraphs: ["Te comprometes a utilizar la aplicación de forma lícita y responsable. Queda prohibido:"],
        listItems: [
          "Usar Cyrqle con fines fraudulentos o ilegales",
          "Intentar acceder sin autorización a sistemas, datos o cuentas de terceros",
          "Interferir en el funcionamiento normal del servicio",
          "Reproducir o distribuir contenidos de la app sin autorización",
        ],
      },
      {
        title: "5. Contenido e interpretaciones",
        paragraphs: [
          "Las visualizaciones, tendencias e insights generados por Cyrqle se basan en la información que introduces sobre ti y tu círculo, y tienen carácter informativo.",
          "No debes tomar decisiones sobre la salud de ningún miembro del círculo basándote únicamente en la aplicación. Consulta siempre con un profesional sanitario.",
        ],
      },
      {
        title: "6. Servicios de inteligencia artificial (Andy)",
        paragraphs: [
          "La aplicación incluye Andy, un asistente de inteligencia artificial que te ayuda a interpretar tus datos de salud y los de tu círculo familiar.",
          "Para generar respuestas, Cyrqle puede enviar datos personales (biomarcadores, resultados de análisis, contexto de salud de la pantalla consultada, mensajes del chat e identificador de cuenta) a Cloudflare Workers AI, proveedor de la tecnología de inteligencia artificial utilizada por Cyrqle, a través del servicio Andy (andy.sentinelzone.com).",
          "Antes de enviar estos datos, la aplicación te informa y solicita tu consentimiento explícito. Puedes retirar este consentimiento en los ajustes de la app.",
          "Estos datos se utilizan exclusivamente para generar respuestas personalizadas a tus preguntas y no se utilizan con fines publicitarios.",
          "Las respuestas de Andy tienen carácter orientativo y no sustituyen la consulta con un profesional sanitario.",
        ],
      },
      {
        title: "7. Propiedad intelectual",
        paragraphs: [
          "Cyrqle, su marca, diseño, software y contenidos están protegidos por derechos de propiedad intelectual.",
          "Se te concede una licencia limitada, personal, no exclusiva e intransferible para usar la app conforme a estos términos.",
        ],
      },
      {
        title: "8. Disponibilidad y cambios",
        paragraphs: [
          "Podemos modificar, suspender o interrumpir funcionalidades del servicio por mantenimiento, mejora o causas operativas.",
          "También podemos actualizar estos términos. Los cambios relevantes se comunicarán en la app o en esta página.",
        ],
      },
      {
        title: "9. Limitación de responsabilidad",
        paragraphs: [
          "En la medida permitida por la ley, Cyrqle no será responsable de daños indirectos derivados del uso o imposibilidad de uso del servicio.",
          "El servicio se ofrece tal cual, sin garantías de resultados de salud o bienestar.",
        ],
      },
      {
        title: "10. Cancelación",
        paragraphs: [
          "Puedes dejar de usar la app en cualquier momento y solicitar la eliminación de tu cuenta y datos conforme a nuestra política de privacidad.",
          "Podemos suspender, restringir o cancelar el acceso si detectamos un uso que incumpla estos términos, incluida la subida de datos de adultos sin consentimiento válido.",
        ],
      },
      {
        title: "11. Contacto",
        paragraphs: ["Para consultas sobre estos términos, escríbenos a:"],
        contact: true,
      },
    ],
  },
  en: {
    pageTitle: "Terms and Conditions – Cyrqle",
    metaDescription:
      "Cyrqle terms: family app, age requirements, circle consent, and rules of use.",
    lastUpdated: "Last updated: June 2026",
    back: "Back to home",
    intro:
      "These terms govern access to and use of Cyrqle, a family wellness application. By using the service, you agree to comply with the conditions below, including age and consent requirements for your circle's data.",
    contactEmail: "legal@sentinelzone.com",
    sections: [
      {
        title: "1. Purpose of the service",
        paragraphs: [
          "Cyrqle is a family wellness application for a responsible adult to organize, interpret, and understand health data for their close circle.",
          "Cyrqle does not provide medical services, diagnoses, or treatments.",
        ],
      },
      {
        title: "2. Eligibility and account",
        paragraphs: ["To use Cyrqle and upload data you must:"],
        listItems: [
          "Be at least 18 years old",
          "Be the account holder and the only person authorized to upload or import health data",
          "Provide accurate information and keep it up to date",
          "Protect access to your account and report unauthorized use",
        ],
      },
      {
        title: "3. Family circle and consent",
        paragraphs: [
          "Cyrqle lets you manage health data for members of your family circle: partner, parents, children, and other close relatives.",
          "The account holder is responsible for the legitimacy of that data.",
        ],
        listItems: [
          "You may enter data for minors when acting as a parent, legal guardian, or authorized representative.",
          "If you upload data for people aged 18 or over, you must have their explicit, informed consent before doing so.",
          "Without that consent, or if you cannot prove it when we request it, we may restrict, suspend, or limit account features until the situation is resolved.",
          "You may not use the app to manage adult circle members' data without their authorization.",
        ],
      },
      {
        title: "4. Permitted use",
        paragraphs: ["You agree to use the application lawfully and responsibly. The following is prohibited:"],
        listItems: [
          "Using Cyrqle for fraudulent or illegal purposes",
          "Attempting unauthorized access to systems, data, or third-party accounts",
          "Interfering with the normal operation of the service",
          "Reproducing or distributing app content without authorization",
        ],
      },
      {
        title: "5. Content and interpretations",
        paragraphs: [
          "Visualizations, trends, and insights generated by Cyrqle are based on information you provide about yourself and your circle, and are for informational purposes only.",
          "You should not make health decisions for any circle member based solely on the application. Always consult a healthcare professional.",
        ],
      },
      {
        title: "6. Artificial intelligence services (Andy)",
        paragraphs: [
          "The application includes Andy, an artificial intelligence assistant that helps you interpret your health data and your family circle's data.",
          "To generate answers, Cyrqle may send personal data (biomarkers, lab results, health context from the screen you ask about, chat messages, and account identifier) to Cloudflare Workers AI, the provider of the artificial intelligence technology used by Cyrqle, through the Andy service (andy.sentinelzone.com).",
          "Before sending this data, the app informs you and asks for your explicit consent. You can withdraw this consent in the app settings.",
          "This data is used solely to generate personalized answers to your questions and is not used for advertising.",
          "Andy's answers are for guidance only and do not replace consultation with a healthcare professional.",
        ],
      },
      {
        title: "7. Intellectual property",
        paragraphs: [
          "Cyrqle, its brand, design, software, and content are protected by intellectual property rights.",
          "You are granted a limited, personal, non-exclusive, non-transferable license to use the app in accordance with these terms.",
        ],
      },
      {
        title: "8. Availability and changes",
        paragraphs: [
          "We may modify, suspend, or discontinue service features for maintenance, improvement, or operational reasons.",
          "We may also update these terms. Relevant changes will be communicated in the app or on this page.",
        ],
      },
      {
        title: "9. Limitation of liability",
        paragraphs: [
          "To the extent permitted by law, Cyrqle shall not be liable for indirect damages arising from use or inability to use the service.",
          "The service is provided as is, without guarantees of health or wellness outcomes.",
        ],
      },
      {
        title: "10. Termination",
        paragraphs: [
          "You may stop using the app at any time and request deletion of your account and data in accordance with our privacy policy.",
          "We may suspend, restrict, or terminate access if we detect use that violates these terms, including uploading adult data without valid consent.",
        ],
      },
      {
        title: "11. Contact",
        paragraphs: ["For questions about these terms, contact us at:"],
        contact: true,
      },
    ],
  },
  fr: {
    pageTitle: "Conditions générales – Cyrqle",
    metaDescription:
      "Conditions Cyrqle : app familiale, âge minimum, consentement du cercle et règles d'utilisation.",
    lastUpdated: "Dernière mise à jour : juin 2026",
    back: "Retour à l'accueil",
    intro:
      "Ces conditions régissent l'accès et l'utilisation de Cyrqle, une application familiale de bien-être. En utilisant le service, vous acceptez de respecter les conditions ci-dessous, y compris les exigences d'âge et de consentement pour les données de votre cercle.",
    contactEmail: "legal@sentinelzone.com",
    sections: [
      {
        title: "1. Objet du service",
        paragraphs: [
          "Cyrqle est une application familiale de bien-être permettant à un adulte responsable d'organiser, interpréter et comprendre les données de santé de son cercle proche.",
          "Cyrqle ne fournit pas de services médicaux, de diagnostics ni de traitements.",
        ],
      },
      {
        title: "2. Éligibilité et compte",
        paragraphs: ["Pour utiliser Cyrqle et téléverser des données, vous devez :"],
        listItems: [
          "Avoir au moins 18 ans",
          "Être titulaire du compte et la seule personne autorisée à téléverser ou importer des données de santé",
          "Fournir des informations exactes et les maintenir à jour",
          "Protéger l'accès à votre compte et signaler toute utilisation non autorisée",
        ],
      },
      {
        title: "3. Cercle familial et consentement",
        paragraphs: [
          "Cyrqle permet de gérer les données de santé des membres de votre cercle familial : conjoint, parents, enfants et autres proches.",
          "La responsabilité de la légitimité de ces données incombe au titulaire du compte.",
        ],
        listItems: [
          "Vous pouvez saisir des données de mineurs lorsque vous agissez en tant que parent, tuteur légal ou représentant autorisé.",
          "Si vous téléversez des données de personnes âgées de 18 ans ou plus, vous devez disposer de leur consentement explicite et éclairé avant de le faire.",
          "Sans ce consentement, ou si vous ne pouvez pas le justifier sur demande, nous pouvons restreindre, suspendre ou limiter les fonctionnalités du compte jusqu'à régularisation.",
          "Il est interdit d'utiliser l'app pour gérer les données d'adultes de votre cercle sans leur autorisation.",
        ],
      },
      {
        title: "4. Utilisation autorisée",
        paragraphs: ["Vous vous engagez à utiliser l'application de manière licite et responsable. Il est interdit de :"],
        listItems: [
          "Utiliser Cyrqle à des fins frauduleuses ou illégales",
          "Tenter d'accéder sans autorisation à des systèmes, données ou comptes tiers",
          "Perturber le fonctionnement normal du service",
          "Reproduire ou distribuer le contenu de l'app sans autorisation",
        ],
      },
      {
        title: "5. Contenu et interprétations",
        paragraphs: [
          "Les visualisations, tendances et insights générés par Cyrqle reposent sur les informations que vous fournissez sur vous et votre cercle, et sont fournis à titre informatif.",
          "Vous ne devez pas prendre de décisions de santé pour un membre du cercle uniquement sur la base de l'application. Consultez toujours un professionnel de santé.",
        ],
      },
      {
        title: "6. Services d'intelligence artificielle (Andy)",
        paragraphs: [
          "L'application inclut Andy, un assistant d'intelligence artificielle qui vous aide à interpréter vos données de santé et celles de votre cercle familial.",
          "Pour générer des réponses, Cyrqle peut envoyer des données personnelles (biomarqueurs, résultats d'analyses, contexte de santé de l'écran consulté, messages du chat et identifiant de compte) à Cloudflare Workers AI, fournisseur de la technologie d'intelligence artificielle utilisée par Cyrqle, via le service Andy (andy.sentinelzone.com).",
          "Avant d'envoyer ces données, l'application vous informe et demande votre consentement explicite. Vous pouvez retirer ce consentement dans les paramètres de l'app.",
          "Ces données sont utilisées exclusivement pour générer des réponses personnalisées à vos questions et ne sont pas utilisées à des fins publicitaires.",
          "Les réponses d'Andy sont fournies à titre indicatif et ne remplacent pas l'avis d'un professionnel de santé.",
        ],
      },
      {
        title: "7. Propriété intellectuelle",
        paragraphs: [
          "Cyrqle, sa marque, son design, son logiciel et son contenu sont protégés par des droits de propriété intellectuelle.",
          "Une licence limitée, personnelle, non exclusive et non transférable vous est accordée pour utiliser l'app conformément à ces conditions.",
        ],
      },
      {
        title: "8. Disponibilité et modifications",
        paragraphs: [
          "Nous pouvons modifier, suspendre ou interrompre des fonctionnalités du service pour maintenance, amélioration ou raisons opérationnelles.",
          "Nous pouvons également mettre à jour ces conditions. Les changements importants seront communiqués dans l'app ou sur cette page.",
        ],
      },
      {
        title: "9. Limitation de responsabilité",
        paragraphs: [
          "Dans la mesure permise par la loi, Cyrqle ne sera pas responsable des dommages indirects résultant de l'utilisation ou de l'impossibilité d'utiliser le service.",
          "Le service est fourni tel quel, sans garantie de résultats de santé ou de bien-être.",
        ],
      },
      {
        title: "10. Résiliation",
        paragraphs: [
          "Vous pouvez cesser d'utiliser l'app à tout moment et demander la suppression de votre compte et de vos données conformément à notre politique de confidentialité.",
          "Nous pouvons suspendre, restreindre ou résilier l'accès en cas d'utilisation contraire à ces conditions, y compris le téléversement de données d'adultes sans consentement valide.",
        ],
      },
      {
        title: "11. Contact",
        paragraphs: ["Pour toute question sur ces conditions, contactez-nous à :"],
        contact: true,
      },
    ],
  },
  ca: {
    pageTitle: "Termes i Condicions – Cyrqle",
    metaDescription:
      "Termes de Cyrqle: app familiar, requisits d'edat, consentiment del cercle i regles d'ús.",
    lastUpdated: "Darrera actualització: juny 2026",
    back: "Tornar a l'inici",
    intro:
      "Aquests termes regulen l'accés i l'ús de Cyrqle, una aplicació familiar de benestar. En utilitzar el servei, acceptes complir les condicions descrites a continuació, inclosos els requisits d'edat i consentiment per a les dades del teu cercle.",
    contactEmail: "legal@sentinelzone.com",
    sections: [
      {
        title: "1. Objecte del servei",
        paragraphs: [
          "Cyrqle és una aplicació familiar de benestar orientada a que un adult responsable organitzi, interpreti i comprengui dades de salut del seu cercle proper.",
          "Cyrqle no presta serveis mèdics, no emet diagnòstics ni prescriu tractaments.",
        ],
      },
      {
        title: "2. Elegibilitat i compte",
        paragraphs: ["Per utilitzar Cyrqle i pujar dades has de:"],
        listItems: [
          "Tenir almenys 18 anys complerts",
          "Ser el titular del compte i l'única persona autoritzada per pujar o importar dades de salut",
          "Proporcionar informació verídica i mantenir-la actualitzada",
          "Protegir l'accés al teu compte i notificar usos no autoritzats",
        ],
      },
      {
        title: "3. Cercle familiar i consentiment",
        paragraphs: [
          "Cyrqle permet gestionar dades de salut dels membres del teu cercle familiar: parella, pares, fills i altres allegats.",
          "La responsabilitat sobre la legitimitat d'aquestes dades recau en el titular del compte.",
        ],
        listItems: [
          "Pots introduir dades de menors d'edat quan actues com a progenitor, tutor legal o responsable legitim.",
          "Si puges dades de persones majors de 18 anys, has de comptar amb el seu consentiment express i informat abans de fer-ho.",
          "Sense aquest consentiment, o si no el pots acreditar quan t'ho sol·licitem, podem restringir, suspendre o limitar funcions del teu compte fins regularitzar la situació.",
          "Queda prohibit utilitzar l'app per gestionar dades d'adults del teu cercle sense la seva autorització.",
        ],
      },
      {
        title: "4. Ús permès",
        paragraphs: ["Et compromets a utilitzar l'aplicació de forma lícita i responsable. Queda prohibit:"],
        listItems: [
          "Utilitzar Cyrqle amb finalitats fraudulentes o il·legals",
          "Intentar accedir sense autorització a sistemes, dades o comptes de tercers",
          "Interferir en el funcionament normal del servei",
          "Reproduir o distribuir continguts de l'app sense autorització",
        ],
      },
      {
        title: "5. Contingut i interpretacions",
        paragraphs: [
          "Les visualitzacions, tendències i insights generats per Cyrqle es basen en la informació que introdueixes sobre tu i el teu cercle, i tenen caràcter informatiu.",
          "No has de prendre decisions sobre la salut de cap membre del cercle basant-te únicament en l'aplicació. Consulta sempre amb un professional sanitari.",
        ],
      },
      {
        title: "6. Serveis d'intel·ligència artificial (Andy)",
        paragraphs: [
          "L'aplicació inclou l'Andy, un assistent d'intel·ligència artificial que t'ajuda a interpretar les teves dades de salut i les del teu cercle familiar.",
          "Per generar respostes, Cyrqle pot enviar dades personals (biomarcadors, resultats d'anàlisis, context de salut de la pantalla consultada, missatges del xat i identificador de compte) a Cloudflare Workers AI, proveïdor de la tecnologia d'intel·ligència artificial utilitzada per Cyrqle, a través del servei Andy (andy.sentinelzone.com).",
          "Abans d'enviar aquestes dades, l'aplicació t'informa i et demana el consentiment explícit. Pots retirar aquest consentiment als ajustos de l'app.",
          "Aquestes dades s'utilitzen exclusivament per generar respostes personalitzades a les teves preguntes i no s'utilitzen amb finalitats publicitàries.",
          "Les respostes de l'Andy tenen caràcter orientatiu i no substitueixen la consulta amb un professional sanitari.",
        ],
      },
      {
        title: "7. Propietat intel·lectual",
        paragraphs: [
          "Cyrqle, la seva marca, disseny, programari i continguts estan protegits per drets de propietat intel·lectual.",
          "Se't concedeix una llicència limitada, personal, no exclusiva i intransferible per utilitzar l'app conforme a aquests termes.",
        ],
      },
      {
        title: "8. Disponibilitat i canvis",
        paragraphs: [
          "Podem modificar, suspendre o interrompre funcionalitats del servei per manteniment, millora o causes operatives.",
          "També podem actualitzar aquests termes. Els canvis rellevants es comunicaran a l'app o en aquesta pàgina.",
        ],
      },
      {
        title: "9. Limitació de responsabilitat",
        paragraphs: [
          "En la mesura permesa per la llei, Cyrqle no serà responsable de danys indirectes derivats de l'ús o impossibilitat d'ús del servei.",
          "El servei s'ofereix tal qual, sense garanties de resultats de salut o benestar.",
        ],
      },
      {
        title: "10. Cancel·lació",
        paragraphs: [
          "Pots deixar d'utilitzar l'app en qualsevol moment i sol·licitar l'eliminació del teu compte i dades conforme a la nostra política de privacitat.",
          "Podem suspendre, restringir o cancel·lar l'accés si detectem un ús que incompleixi aquests termes, inclosa la pujada de dades d'adults sense consentiment vàlid.",
        ],
      },
      {
        title: "11. Contacte",
        paragraphs: ["Per a consultes sobre aquests termes, escriu-nos a:"],
        contact: true,
      },
    ],
  },
};
